import { COUNTRY_CODES, INITIAL_SNACKBAR_DATA } from "../../common/constants";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
	EnhancedDropDownRHFWithLabel,
	EnhancedSnackbar,
	LabelPosition,
	RHookFormMaskedTextFieldWithLabel,
	RHookFormTextFieldWithLabel,
	Spacer,
	SubmitButton,
	SubmitButtonVariant,
} from "@websential/cosmic-v20";
import {
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";

import { AccountFormLayout } from "../AccountFormLayout";
import { AuthService } from "../../services/AuthService";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact/EnhancedButtonPrimeReact";
import { EnhancedButtonPrimeReactSeverity } from "../../common/Common";
import { PhoneNumberValidationSchema } from "./validator";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

type FormValues = {
	phone: string;
	countryCode?: string | null;
	otp?: string | null;
};


const AddPhoneValidation = () => {
	const formMethods = useForm<any>({
		resolver: yupResolver(PhoneNumberValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: {
			countryCode: "+1",
		},
	});
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [showOtpField, setShowOtpField] = useState(false);
	const [CountryCodeValue, setCountryCodeValue] = useState<any>("+1");
	const [PhoneNumberPlaceholder, setPhoneNumberPlaceholder] =
		useState<any>("(123) 456-7890");
	const [PhoneNumberInputMaskPattern, setPhoneNumberInputMaskPattern] =
		useState<any>("(000) 000-0000");
	const accessToken = localStorage.getItem("accessToken");
	const navigate = useNavigate();

	const sendOtpMutation: any = useMutation({
		mutationFn: AuthService.CreateOtpByMessegeForPhoneNumberSave,
		onSuccess: (res: any) => {
			setSnackbarData({
				open: true,
				message: res.message,
				severity: res.status ? "success" : "error",
			});
			if (res.status) {
				localStorage.setItem("accessToken", res.accessToken);
				setShowOtpField(true);
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
			setShowOtpField(false);
		},
	});

	const verifyOtpMutation: any = useMutation({
		mutationFn: AuthService.VerifyOtpByMessegeForPhoneNumberSave,
		onSuccess: (res: any) => {
			if (res.status) {
				setSnackbarData({
					open: true,
					message:
						res.message || "Phone number verified and added successfully.",
					severity: "success",
				});

				// Delay navigation slightly to allow snackbar to be seen
				setTimeout(() => {
					AuthService.CreateOtpByMessege(accessToken);
					navigate("/phone-otp-validation", {
						state: {
							message: "Phone number verified successfully. Please sign in.",
						},
					});
				}, 2000);
				// navigate("/login", {
				// 	state: { message: "Phone number verified successfully. Please sign in." },
				// });
			} else {
				setSnackbarData({
					open: true,
					message:
						res.message || "Phone number verified and added successfully.",
					severity: "error",
				});
			}
		},
		onError: () => {
			setSnackbarData({
				open: true,
				message: "OTP verification failed. Try again!",
				severity: "error",
			});
		},
	});

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") return;
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const onSubmit = (formValues: FormValues) => {
		const fullPhone = `${formValues.countryCode}${formValues.phone}`;

		if (showOtpField) {
			const payload = {
				accessToken: localStorage.getItem("accessToken"),
				phone: fullPhone,
				otp: formValues.otp,
			};
			verifyOtpMutation.mutate(payload);
		} else {
			const payload = {
				accessToken: localStorage.getItem("accessToken"),
				phone: fullPhone,
			};
			sendOtpMutation.mutate(payload);
		}
	};

	const onCountryCodeSelectionChangeHandler = (selectedCountry: any) => {
		const foundCountry = COUNTRY_CODES.find(
			(c) => c.value === selectedCountry.value
		);
		if (foundCountry) {
			formMethods.setValue("phone", "");
			setCountryCodeValue(foundCountry.value);
			setPhoneNumberInputMaskPattern(foundCountry.mask);
			setPhoneNumberPlaceholder(foundCountry.placeholder);
		}
	};

	return (
		<>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid
							size={{ xs: 12, sm: 5, md: 4, lg: 4, xl: 4 }}
						// sx={{ display: "flex", alignItems: "center" }}
						>
							<EnhancedDropDownRHFWithLabel
								label="Country Code"
								testId="country-code-field"
								selectName="countryCode"
								items={COUNTRY_CODES}
								// onSelectionChangeHandler={(val) =>
								// 	setCountryCodeValue(val.value)
								// }
								onSelectionChangeHandler={onCountryCodeSelectionChangeHandler}
								selectedItem={CountryCodeValue}
							/>
						</Grid>

						<Grid size={{ xs: 12, sm: 7, md: 8, lg: 8, xl: 8 }}
							sx={{
								display: "flex", alignItems: "center",
								"& .MuiGrid-root": { width: "100%" }
							}}
						>
							<div
								style={{
									marginBottom: "0.7rem",
									marginRight: "1rem",
									color: "rgba(0, 0, 0, 0.6)",
								}}
							>
								{CountryCodeValue}
							</div>
							{/* <RHookFormTextFieldWithLabel
								label="Phone Number"
								fieldValue="phone"
								id="phone"
								testId="phone-field"
								placeholder={PhoneNumberPlaceholder}
							/> */}
							<RHookFormMaskedTextFieldWithLabel
								label="Phone Number"
								id="phone"
								testId="phone-field"
								labelPosition={LabelPosition.Top}
								placeholder={PhoneNumberPlaceholder}
								inputMaskPattern={PhoneNumberInputMaskPattern}
							/>
						</Grid>

						{showOtpField && (
							<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
								<RHookFormTextFieldWithLabel
									label="Enter OTP"
									fieldValue="otp"
									id="otp"
									testId="otp-field"
									placeholder="Enter OTP"
								/>
							</Grid>
						)}


						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<EnhancedButtonPrimeReact
								label={showOtpField ? "Verify OTP" : "Send OTP"}
								raised
								type="submit"
								style={{ marginLeft: "0rem" }}
								loading={
									sendOtpMutation.isPending || verifyOtpMutation.isPending
								}
								severity={EnhancedButtonPrimeReactSeverity.Success}
							/>
						</Grid>


						<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
							<Typography variant="body2" color="textSecondary">
								We'll send you a one-time code via SMS.
							</Typography>
						</Grid>
					</Grid>
				</form>
			</FormProvider>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId="phone-form-snackbar"
				open={snackbarData.open}
				autoHide={true}
			/>
		</>
	);
};

export const AddPhoneValidationComponent = () => {
	return (
		<AccountFormLayout
			title="Add and Verify Cell No."
			subtitle="Having trouble with this method?"
			subtitleLinkText="Select another method."
			subtitleLinkNavigateTo="/auth-selection-screen-for-login"
			form={<AddPhoneValidation />}
		/>
	);
};
